// Search and filter helpers for equipment search functionality

import type {
  EquipmentAttributeDefinition,
  FilterCriteria,
} from "../api/equipment/Equipment.typed";

/** True when the attribute is edited as a closed set of labels (model type list, or string/comments with options). */
export function equipmentAttributeUsesEnumPicklist(attr: EquipmentAttributeDefinition): boolean {
  if (attr.Type === "list") return true;

  if (attr.Type !== "string" && attr.Type !== "comments") return false;

  return (attr.Options?.length ?? 0) > 0;
}

/** List-field sentinel → API sends Value "" with Operator "=" (attribute missing or empty text). */
export const EQUIPMENT_LIST_FILTER_EMPTY_SENTINEL = "__equipment_list_filter_empty__";

/** Legacy prefilter token → API: Value "" + Operator "!=". No longer offered in UI selects. */
export const EQUIPMENT_LIST_FILTER_NONEMPTY_SENTINEL = "__equipment_list_filter_nonempty__";

/** Virtual operator tokens that map to (Operator, "") at submit; never sent as-is. */
export const OPERATOR_EMPTY_SENTINEL = "__op_empty__";
export const OPERATOR_NOT_EMPTY_SENTINEL = "__op_not_empty__";

export function isEmptyOperatorSentinel(op: string): boolean {
  return op === OPERATOR_EMPTY_SENTINEL || op === OPERATOR_NOT_EMPTY_SENTINEL;
}

export function resolveEmptyOperatorSentinel(
  op: string,
): { operator: "=" | "!="; value: "" } | null {
  if (op === OPERATOR_EMPTY_SENTINEL) return { operator: "=", value: "" };
  if (op === OPERATOR_NOT_EMPTY_SENTINEL) return { operator: "!=", value: "" };

  return null;
}

export function resolveEquipmentListFilterSentinel(
  raw: string,
): { value: string; operator: "=" | "!=" } | null {
  if (raw === EQUIPMENT_LIST_FILTER_EMPTY_SENTINEL) return { value: "", operator: "=" };
  if (raw === EQUIPMENT_LIST_FILTER_NONEMPTY_SENTINEL) return { value: "", operator: "!=" };

  return null;
}

/** Maps stored API empty + equality operators back to sentinel option values so the picker shows the selection. */
export function equipmentPicklistSelectDisplayValue(
  operator: string,
  value: string,
): string | null {
  const trimmed = (value ?? "").trim();
  if (trimmed !== "") return value;

  switch (operator) {
    case "=":
      return EQUIPMENT_LIST_FILTER_EMPTY_SENTINEL;
    default:
      return null;
  }
}

/** Chip / summary text for a stored criterion (handles empty-value semantics from the API). */
export function formatEquipmentFilterValueSnippet(
  criteria: Pick<FilterCriteria, "Value" | "Operator">,
): string {
  const trimmed = criteria.Value.trim();
  if (trimmed.length > 0) return trimmed;

  if (criteria.Operator === "=") return "non renseigné";

  if (criteria.Operator === "!=" || criteria.Operator === "<>") return "renseigné";

  return "—";
}

export interface OperatorOption {
  value: string;
  label: string;
}

/** Two virtual operators appended to every list to filter empty/non-empty values without a value input. */
const EMPTY_OPERATOR_OPTIONS: OperatorOption[] = [
  { value: OPERATOR_EMPTY_SENTINEL, label: "est vide" },
  { value: OPERATOR_NOT_EMPTY_SENTINEL, label: "n'est pas vide" },
];

/**
 * Get available operators for a given attribute type
 */
export const getOperatorOptions = (attrType: string): OperatorOption[] => {
  if (attrType === "string" || attrType === "list" || attrType === "comments") {
    return [
      { value: "ILIKE", label: "contient" },
      { value: "NOT ILIKE", label: "ne contient pas" },
      { value: "=", label: "égal à" },
      { value: "!=", label: "différent de" },
      ...EMPTY_OPERATOR_OPTIONS,
    ];
  }
  return [
    { value: "=", label: "égal à" },
    { value: "!=", label: "différent de" },
    { value: ">", label: "supérieur à" },
    { value: "<", label: "inférieur à" },
    { value: ">=", label: "supérieur ou égal à" },
    { value: "<=", label: "inférieur ou égal à" },
    ...EMPTY_OPERATOR_OPTIONS,
  ];
};

/**
 * Get human-readable label for an operator
 */
export const getOperatorLabel = (operator: string): string => {
  const operatorLabels: Record<string, string> = {
    "=": "égal à",
    "!=": "différent de",
    ">": "supérieur à",
    "<": "inférieur à",
    ">=": "supérieur ou égal à",
    "<=": "inférieur ou égal à",
    ILIKE: "contient",
    "NOT ILIKE": "ne contient pas",
    "NOT LIKE": "ne contient pas",
  };
  return operatorLabels[operator] || operator;
};

/**
 * Get default operator for a given attribute type
 */
export const getDefaultOperator = (attrType: string): string => {
  if (attrType === "string" || attrType === "list" || attrType === "comments") {
    return "ILIKE";
  }
  return "=";
};

export const getDefaultOperatorForAttribute = (attr?: EquipmentAttributeDefinition): string => {
  if (!attr) return "=";

  if (equipmentAttributeUsesEnumPicklist(attr)) return "ILIKE";

  return getDefaultOperator(attr.Type);
};

/**
 * Operator set for the live search bar: enum-like fields use the same choices as free-text string search.
 */
export const getOperatorOptionsForAttribute = (
  attr: EquipmentAttributeDefinition | undefined,
): OperatorOption[] => {
  if (!attr) return getOperatorOptions("string");

  if (equipmentAttributeUsesEnumPicklist(attr)) return getOperatorOptions("list");

  return getOperatorOptions(attr.Type);
};

/**
 * Get input type for HTML input based on attribute type
 */
export const getInputType = (attrType: string): string => {
  const typeMap: Record<string, string> = {
    number: "number",
    // Use text for date to allow relative tokens (now, today, now±Nd/Nw/NM/Ny)
    date: "text",
    string: "text",
    comments: "text",
  };
  return typeMap[attrType] || "text";
};
